import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Lock, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth.jsx";
import { formatApiError } from "@/lib/api";
import { LOGO_URL, COMPANY } from "@/lib/constants";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Willkommen zurück!");
      nav("/crm");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAFAF5]">
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#5C6B5D] hover:text-[#6B7F3E] mb-8" data-testid="login-back-link">
            <ArrowLeft className="w-4 h-4" /> Zur Startseite
          </Link>
          <div className="flex items-center gap-3 mb-8">
            <img src={LOGO_URL} alt="" className="h-12 w-auto" />
            <div>
              <div className="font-display text-lg font-semibold text-[#1A201A]">{COMPANY.name}</div>
              <div className="text-xs text-[#6B7F3E]">CRM-Portal</div>
            </div>
          </div>
          <h1 className="font-display text-3xl font-semibold text-[#1A201A] tracking-tight mb-2">
            Willkommen zurück.
          </h1>
          <p className="text-sm text-[#5C6B5D] mb-8">
            Melden Sie sich mit Ihren Zugangsdaten an.
          </p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Benutzername</Label>
              <Input
                id="email" data-testid="login-email-input" type="text" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5" autoComplete="username"
                placeholder="z. B. m.blessing"
              />
            </div>
            <div>
              <Label htmlFor="password">Passwort</Label>
              <Input
                id="password" data-testid="login-password-input" type="password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5" autoComplete="current-password"
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={busy}
              className="w-full bg-[#6B7F3E] hover:bg-[#5A6B34] text-white h-10 mt-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4 mr-1.5" /> Anmelden</>}
            </Button>
            <div className="text-center pt-1">
              <Link
                to="/forgot-password"
                data-testid="login-forgot-link"
                className="text-xs text-[#6B7F3E] hover:text-[#5A6B34] hover:underline"
              >
                Passwort vergessen?
              </Link>
            </div>
          </form>
        </div>
      </div>
      <div className="hidden lg:block relative bg-[#2C3E2D]">
        <img
          src="https://images.unsplash.com/photo-1734303023491-db8037a21f09?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1A201A]/80 via-[#2C3E2D]/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <div className="font-display text-3xl leading-tight max-w-md">
            „Alles rund ums Haus – <span className="text-[#C4CFB1]">Rundum-Sorglos-Paket</span>."
          </div>
          <div className="mt-3 text-sm text-[#B8C1A9]">
            Interne Prozessautomatisierung für {COMPANY.name}
          </div>
        </div>
      </div>
    </div>
  );
}
